import React, { memo, useEffect, useState } from 'react';
import { TextField, TextFieldProps } from '@mui/material';
import useDebounce from '../hooks/useDebounce';

/**
 * Props specific to DelayedTextField (beyond standard MUI TextField props).
 * The `onChange` callback receives only the debounced string value, not a React event.
 */
interface DelayedTextFieldProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  value?: string;
  delay?: number;
  onChange: (value: string) => void;
}

/**
 * A MUI TextField that debounces its value before calling `onChange`.
 * Useful for search inputs where you want to wait for the user to stop typing.
 */
const DelayedTextField = (props: DelayedTextFieldProps) => {
  const { value = '', delay = 500, onChange, ...other } = props;
  const [localValue, setLocalValue] = useState<string>(value);
  const debouncedValue = useDebounce(localValue, delay);

  useEffect(() => {
    let ignore = false;
    if (!ignore) {
      onChange(debouncedValue);
    }
    return () => {
      ignore = true;
    };
  }, [debouncedValue, localValue, onChange]);

  return (
    <TextField
      {...other}
      value={localValue}
      onChange={(event: React.ChangeEvent<HTMLInputElement>) => setLocalValue(event.target.value)}
    />
  );
};

export default memo(DelayedTextField);
